// src/pages/finance-portal/cash-application/CashApplication.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, Skeleton, Tooltip,
    InputAdornment, Autocomplete, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions,
    LinearProgress,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const PAYMENT_METHOD_LABELS = {
    CASH: 'Cash',
    CHECK: 'Check',
    BANK_TRANSFER: 'Bank Transfer',
    CREDIT_CARD: 'Credit Card',
    DEBIT_CARD: 'Debit Card',
    ACH: 'ACH',
    ONLINE: 'Online',
    OTHER: 'Other',
};

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getConfidenceColor(confidence) {
    const pct = confidence * 100;
    if (pct >= 90) return { bg: '#dcfce7', color: '#166534' };
    if (pct >= 70) return { bg: '#fef3c7', color: '#92400e' };
    return { bg: '#fecaca', color: '#991b1b' };
}

export default function CashApplication() {
    const [payments, setPayments] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [search, setSearch] = useState('');

    // Auto-Match dialog
    const [autoMatchDialog, setAutoMatchDialog] = useState({ open: false, payment: null });
    const [autoMatchResults, setAutoMatchResults] = useState([]);
    const [autoMatching, setAutoMatching] = useState(false);
    const [applyingSuggestion, setApplyingSuggestion] = useState(null);

    // Manual Apply dialog
    const [manualDialog, setManualDialog] = useState({ open: false, payment: null });
    const [manualInvoice, setManualInvoice] = useState(null);
    const [manualAmount, setManualAmount] = useState('');
    const [manualApplying, setManualApplying] = useState(false);
    const [invoiceOptions, setInvoiceOptions] = useState([]);
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const invoiceTimer = useRef(null);

    const fetchUnapplied = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (search) params.search = search;

            const res = await axiosInstance.get('/api/finance/cash-application/unapplied', { params });
            if (res.data.success) {
                setPayments(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load unapplied payments');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, search]);

    useEffect(() => { fetchUnapplied(); }, [fetchUnapplied]);

    // Invoice search for manual apply (filtered to customer's unpaid invoices)
    useEffect(() => {
        if (invoiceTimer.current) clearTimeout(invoiceTimer.current);
        if (!manualDialog.open || !manualDialog.payment?.customer_id) {
            setInvoiceOptions([]);
            return;
        }
        setInvoiceLoading(true);
        invoiceTimer.current = setTimeout(async () => {
            try {
                const params = {
                    customer_id: manualDialog.payment.customer_id,
                    status: 'unpaid',
                    per_page: 20,
                };
                if (invoiceSearch) params.search = invoiceSearch;
                const res = await axiosInstance.get('/api/finance/invoices', { params });
                if (res.data.success) {
                    setInvoiceOptions(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to fetch invoices', err);
            } finally {
                setInvoiceLoading(false);
            }
        }, 300);
        return () => { if (invoiceTimer.current) clearTimeout(invoiceTimer.current); };
    }, [manualDialog.open, manualDialog.payment?.customer_id, invoiceSearch]);

    const handleAutoMatch = async (payment) => {
        setAutoMatchDialog({ open: true, payment });
        setAutoMatchResults([]);
        setAutoMatching(true);
        setError(null);
        try {
            const res = await axiosInstance.post('/api/finance/cash-application/auto-apply', {
                payment_id: payment.id,
            });
            if (res.data.success) {
                setAutoMatchResults(res.data.data?.suggestions || res.data.data || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Auto-match failed');
            setAutoMatchDialog({ open: false, payment: null });
        } finally {
            setAutoMatching(false);
        }
    };

    const handleApplySuggestion = async (suggestion) => {
        setApplyingSuggestion(suggestion.invoice_id);
        setError(null);
        try {
            await axiosInstance.post('/api/finance/cash-application/apply', {
                payment_id: autoMatchDialog.payment.id,
                invoice_id: suggestion.invoice_id,
                amount: suggestion.amount,
            });
            setSuccess(`Payment applied to invoice ${suggestion.invoice_number || suggestion.invoice_id} successfully`);
            setAutoMatchDialog({ open: false, payment: null });
            setAutoMatchResults([]);
            fetchUnapplied();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to apply payment');
        } finally {
            setApplyingSuggestion(null);
        }
    };

    const openManualDialog = (payment) => {
        setManualDialog({ open: true, payment });
        setManualInvoice(null);
        setManualAmount(payment.amount ? String(payment.amount) : '');
        setInvoiceSearch('');
    };

    const handleManualApply = async () => {
        if (!manualInvoice || !manualAmount) return;
        setManualApplying(true);
        setError(null);
        try {
            await axiosInstance.post('/api/finance/cash-application/apply', {
                payment_id: manualDialog.payment.id,
                invoice_id: manualInvoice.id,
                amount: parseFloat(manualAmount),
            });
            setSuccess(`Payment applied to invoice ${manualInvoice.invoice_number || manualInvoice.id} successfully`);
            setManualDialog({ open: false, payment: null });
            fetchUnapplied();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to apply payment');
        } finally {
            setManualApplying(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <AutoFixHighIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Cash Application</Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Match and apply unapplied payments to invoices</Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {/* Section Title */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ReceiptIcon sx={{ color: '#64748b', fontSize: 20 }} />
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>Unapplied Payments</Typography>
                    {total > 0 && (
                        <Chip
                            label={total}
                            size="small"
                            sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 700, height: 22, bgcolor: '#fef3c7', color: '#92400e' }}
                        />
                    )}
                </Box>

                {/* Search */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search unapplied payments..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { fontFamily, borderRadius: '10px' },
                        }}
                        sx={{ minWidth: 280 }}
                    />
                </Box>

                {/* Unapplied Payments Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Payment #', 'Customer', 'Date', 'Amount', 'Method', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>
                                            {[1, 2, 3, 4, 5, 6].map(j => (
                                                <TableCell key={j}><Skeleton height={36} /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : payments.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                            <CheckCircleIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No unapplied payments</Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem', mt: 0.5 }}>All payments have been applied to invoices</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : payments.map(payment => {
                                    const customerName = payment.customer_name || payment.customer?.company_name || payment.customer?.name || '\u2014';
                                    const methodLabel = PAYMENT_METHOD_LABELS[payment.payment_method] || payment.payment_method || '\u2014';

                                    return (
                                        <TableRow key={payment.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {payment.payment_number || `PMT-${payment.id}`}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {customerName}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {formatDate(payment.payment_date)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(payment.amount)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {methodLabel}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Auto-Match">
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<SmartToyIcon sx={{ fontSize: 16 }} />}
                                                            onClick={() => handleAutoMatch(payment)}
                                                            sx={{
                                                                fontFamily, textTransform: 'none', fontSize: '0.75rem',
                                                                borderRadius: '8px', fontWeight: 600,
                                                                borderColor: ACCENT, color: ACCENT,
                                                                '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' },
                                                            }}
                                                        >
                                                            Auto-Match
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title="Apply Manually">
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                                                            onClick={() => openManualDialog(payment)}
                                                            sx={{
                                                                fontFamily, textTransform: 'none', fontSize: '0.75rem',
                                                                borderRadius: '8px', fontWeight: 600,
                                                                borderColor: '#94a3b8', color: '#64748b',
                                                                '&:hover': { borderColor: '#64748b', bgcolor: '#f8fafc' },
                                                            }}
                                                        >
                                                            Apply Manually
                                                        </Button>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Auto-Match Results Dialog */}
            <Dialog
                open={autoMatchDialog.open}
                onClose={() => { setAutoMatchDialog({ open: false, payment: null }); setAutoMatchResults([]); }}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SmartToyIcon sx={{ color: ACCENT }} />
                    Auto-Match Results
                    {autoMatchDialog.payment && (
                        <Chip
                            label={autoMatchDialog.payment.payment_number || `PMT-${autoMatchDialog.payment.id}`}
                            size="small"
                            sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, ml: 1, bgcolor: '#f1f5f9', color: '#475569' }}
                        />
                    )}
                </DialogTitle>
                <DialogContent>
                    {autoMatchDialog.payment && (
                        <Box sx={{ display: 'flex', gap: 4, mb: 3, p: 2, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Customer</Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>
                                    {autoMatchDialog.payment.customer_name || autoMatchDialog.payment.customer?.company_name || '\u2014'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Amount</Typography>
                                <Typography sx={{ fontFamily, fontSize: '1rem', color: ACCENT, fontWeight: 700 }}>
                                    {formatCurrency(autoMatchDialog.payment.amount)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Date</Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>
                                    {formatDate(autoMatchDialog.payment.payment_date)}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {autoMatching ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress size={36} sx={{ color: ACCENT, mb: 2 }} />
                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem' }}>Analyzing payment and finding matching invoices...</Typography>
                            <LinearProgress sx={{ mt: 2, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: ACCENT } }} />
                        </Box>
                    ) : autoMatchResults.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <SearchIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No matching invoices found</Typography>
                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem', mt: 0.5 }}>Try applying this payment manually instead</Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        {['Invoice #', 'Amount', 'Due Date', 'Match Confidence', 'Action'].map(h => (
                                            <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {autoMatchResults.map((suggestion, idx) => {
                                        const confidence = suggestion.confidence || suggestion.match_confidence || 0;
                                        const pct = Math.round(confidence * 100);
                                        const confColor = getConfidenceColor(confidence);

                                        return (
                                            <TableRow key={suggestion.invoice_id || idx} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                    {suggestion.invoice_number || `INV-${suggestion.invoice_id}`}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                                                    {formatCurrency(suggestion.amount || suggestion.invoice_amount)}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                    {formatDate(suggestion.due_date)}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{ flex: 1, maxWidth: 100 }}>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={pct}
                                                                sx={{
                                                                    height: 8, borderRadius: 4,
                                                                    bgcolor: '#f1f5f9',
                                                                    '& .MuiLinearProgress-bar': {
                                                                        bgcolor: confColor.color,
                                                                        borderRadius: 4,
                                                                    },
                                                                }}
                                                            />
                                                        </Box>
                                                        <Chip
                                                            label={`${pct}%`}
                                                            size="small"
                                                            sx={{
                                                                fontFamily, fontSize: '0.7rem', fontWeight: 700, height: 22,
                                                                bgcolor: confColor.bg, color: confColor.color,
                                                            }}
                                                        />
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        onClick={() => handleApplySuggestion(suggestion)}
                                                        disabled={applyingSuggestion === suggestion.invoice_id}
                                                        startIcon={applyingSuggestion === suggestion.invoice_id ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                                                        sx={{
                                                            fontFamily, textTransform: 'none', fontSize: '0.75rem',
                                                            borderRadius: '8px', fontWeight: 600,
                                                            bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' },
                                                        }}
                                                    >
                                                        {applyingSuggestion === suggestion.invoice_id ? 'Applying...' : 'Apply'}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => { setAutoMatchDialog({ open: false, payment: null }); setAutoMatchResults([]); }}
                        sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}
                    >
                        Close
                    </Button>
                    {autoMatchResults.length === 0 && !autoMatching && autoMatchDialog.payment && (
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setAutoMatchDialog({ open: false, payment: null });
                                setAutoMatchResults([]);
                                openManualDialog(autoMatchDialog.payment);
                            }}
                            startIcon={<EditIcon />}
                            sx={{
                                fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '10px',
                                borderColor: ACCENT, color: ACCENT,
                                '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' },
                            }}
                        >
                            Apply Manually
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Manual Apply Dialog */}
            <Dialog
                open={manualDialog.open}
                onClose={() => setManualDialog({ open: false, payment: null })}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EditIcon sx={{ color: ACCENT }} />
                    Apply Payment Manually
                </DialogTitle>
                <DialogContent>
                    {manualDialog.payment && (
                        <Box sx={{ display: 'flex', gap: 4, mb: 3, p: 2, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Payment</Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 600 }}>
                                    {manualDialog.payment.payment_number || `PMT-${manualDialog.payment.id}`}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Customer</Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b', fontWeight: 500 }}>
                                    {manualDialog.payment.customer_name || manualDialog.payment.customer?.company_name || '\u2014'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>Amount</Typography>
                                <Typography sx={{ fontFamily, fontSize: '1rem', color: ACCENT, fontWeight: 700 }}>
                                    {formatCurrency(manualDialog.payment.amount)}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Autocomplete
                            size="small"
                            options={invoiceOptions}
                            getOptionLabel={opt => opt ? `${opt.invoice_number || 'INV-???'} - ${formatCurrency(opt.total || opt.amount_due)} (Due: ${formatDate(opt.due_date)})` : ''}
                            value={manualInvoice}
                            onChange={(_, val) => {
                                setManualInvoice(val);
                                if (val && (val.amount_due || val.total)) {
                                    const invoiceAmount = val.amount_due || val.total;
                                    const paymentAmount = manualDialog.payment?.amount || 0;
                                    setManualAmount(String(Math.min(invoiceAmount, paymentAmount)));
                                }
                            }}
                            onInputChange={(_, val) => setInvoiceSearch(val)}
                            loading={invoiceLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Invoice"
                                    placeholder="Search unpaid invoices..."
                                    InputLabelProps={{ ...params.InputLabelProps, sx: { fontFamily } }}
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { fontFamily, borderRadius: '10px' },
                                        endAdornment: (
                                            <>
                                                {invoiceLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText={manualDialog.payment?.customer_id ? "No unpaid invoices found for this customer" : "Select a payment with a customer"}
                        />

                        <TextField
                            size="small"
                            label="Amount to Apply"
                            type="number"
                            value={manualAmount}
                            onChange={e => setManualAmount(e.target.value)}
                            InputLabelProps={{ sx: { fontFamily } }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                sx: { fontFamily, borderRadius: '10px' },
                            }}
                            helperText={manualDialog.payment ? `Payment total: ${formatCurrency(manualDialog.payment.amount)}` : ''}
                            FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.75rem' } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setManualDialog({ open: false, payment: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleManualApply}
                        disabled={manualApplying || !manualInvoice || !manualAmount}
                        startIcon={manualApplying ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {manualApplying ? 'Applying...' : 'Apply Payment'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
